
// Forza TS a trattare il modulo come any
// eslint-disable-next-line @typescript-eslint/no-var-requires
import { goapi } from '../../api'
import { getBlurFilterRadius } from '../../utils/blurFilters'
import Vue from 'vue'

// eslint-disable-next-line @typescript-eslint/no-var-requires
const PlatformDetection = require('../../utils/platformDetection') as any

const { requiresIOSVideoWorkaround, getPlatformConstraints, getPlatformInfo } = PlatformDetection

interface Participant {
	name: string
	avatarColor?: string
}

interface BackgroundImage {
	image: string
	tooltip: string
}

declare global {
	interface Window {
		TwilioVideo: {
			createLocalVideoTrack: (constraints?: any) => Promise<any>
			createLocalAudioTrack: (constraints?: any) => Promise<any>
		}
	}
}

interface ComponentData {
	permissions: {
		audio: boolean
		video: boolean
	}
	platformInfo: ReturnType<typeof getPlatformInfo>
	partecipantName: string
	partecipantSurname: string
	partecipantEmail: string
	errors: string
	modal: boolean
	enableVideo: boolean
	enableAudio: boolean
	loadingParticipants: boolean
	partecipants: Participant[]
	meetName: string
	mainVideoTrack: any
	mainAudioTrack: any
	isJoining: boolean
	blur: number
	background: BackgroundImage | null
	currentProcessor: any
	participantsInterval: number | undefined
	videoProcessors: any
	virtualBackgroundProcessor: any
	backgroundsLoading: boolean
	backgrounds: BackgroundImage[]
	image: any
	isIOSDevice: boolean
	inputFocused: string
	oldEffects: {
		blur: number
		background: BackgroundImage | null
	}
	emailErrors: boolean
	checkVideoLoading: boolean
	hideOthers: number
	tooltipVisible: boolean
	tooltipContent: string
	tooltipStyle: any
}

export default Vue.extend({
	data(): ComponentData {
		return {
			tooltipVisible: false,
			tooltipContent: '',
			tooltipStyle: {},
			hideOthers: 10,
			checkVideoLoading: true,
			permissions: { audio: false, video: false },
			errors: '',
			emailErrors: false,
			oldEffects: { blur: 0, background: null },
			inputFocused: '',
			platformInfo: getPlatformInfo(),
			partecipantName: '',
			partecipantSurname: '',
			partecipantEmail: '',
			modal: false,
			enableVideo: false,
			enableAudio: false,
			loadingParticipants: false,
			partecipants: [],
			meetName: this.$t('video_meet').toString(),
			mainVideoTrack: null,
			mainAudioTrack: null,
			isJoining: false,
			blur: 0,
			background: null,
			currentProcessor: null,
			participantsInterval: undefined,
			videoProcessors: null,
			virtualBackgroundProcessor: null,
			backgroundsLoading: false,
			backgrounds: [
				{ image: '/backgrounds/office1.jpg', tooltip: 'Contemporarary open space' },
				{ image: '/backgrounds/office2.jpg', tooltip: 'Cozy workplace at home' },
				{ image: '/backgrounds/office3.jpg', tooltip: 'Simple office' },
				{ image: '/backgrounds/office4.jpg', tooltip: 'Houseplants background' },
				{ image: '/backgrounds/office5.jpg', tooltip: 'Creative office' },
				{ image: '/backgrounds/office6.jpg', tooltip: 'Modern office' },
				{ image: '/backgrounds/office7.jpg', tooltip: 'Medical office' },
				{ image: '/backgrounds/office8.jpg', tooltip: 'Modern white office' },
				{ image: '/backgrounds/office9.jpg', tooltip: 'Office background' },
				{ image: '/backgrounds/office10.jpg', tooltip: 'Office interior' },
				{ image: '/backgrounds/office11.jpg', tooltip: 'Office with various plants' }
				/* 	{ image: '/backgrounds/office12.jpg', tooltip: 'Office room with a white background' } */
			],
			image: null,
			isIOSDevice: requiresIOSVideoWorkaround()
		}
	},
	watch: {
		modal(val: boolean): void {
			if (val) {
				document.body.style.overflow = 'hidden'
			} else {
				document.body.style.overflow = ''
			}
		}
	},
	async mounted(): Promise<void> {
		// Log platform info for debugging
		console.log('Preview platform info:', this.platformInfo)

		// Initialize tracks with default enabled state
		this.enableVideo = true
		this.enableAudio = true
		await this.initializeTracks()

		// Carica partecipanti attuali della room
		this.loadRoomParticipants()

		// Polling per aggiornamenti ogni 5 secondi
		this.participantsInterval = setInterval(() => {
			this.loadRoomParticipants()
		}, 5000)
	},
	methods: {
		showTooltip(event: MouseEvent, name: string) {
			const avatar = event.currentTarget as HTMLElement
			if (!avatar) return
			const content = this.getNameAndSurname({ name })
			const rect = avatar.getBoundingClientRect()

			this.tooltipContent = content
			this.tooltipStyle = {
				left: `${rect.left + rect.width / 2}px`,
				top: `${rect.top - 10}px`,
				transform: 'translate(-50%, -100%)'
			}

			this.tooltipVisible = true
		},

		showMoreTooltip(event: MouseEvent) {
			const element = event.currentTarget as HTMLElement
			if (!element) return

			const rect = element.getBoundingClientRect()

			const otherParticipants = this.partecipants.slice(this.hideOthers)
			const participantsList = otherParticipants
				.map((p: any, index: number) => {
					const comma = index < otherParticipants.length - 1 ? ', ' : ''
					return `${this.getNameAndSurname(p)}${comma}`
				})
				.join('')

			this.tooltipContent = `<div style="white-space: normal; word-wrap: break-word; max-width: 300px;">${participantsList}</div>
`

			this.tooltipStyle = {
				left: `${rect.left + rect.width / 2}px`,
				top: `${rect.top - 10}px`,
				transform: 'translate(-50%, -100%)'
			}

			this.tooltipVisible = true
		},

		hideTooltip() {
			this.tooltipVisible = false
		},
		isMobile() {
			return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
		},
		async initializeTracks(): Promise<void> {
			// Initialize video track based on default state
			if (this.enableVideo) {
				await this.createVideoTrack()
			}

			// ALWAYS create audio track (for unified audio management)
			// We can enable/disable it later based on user preference
			await this.createAudioTrack()

			// Set the audio track enabled state based on user preference
			if (this.mainAudioTrack) {
				if (this.enableAudio) {
					this.mainAudioTrack.enable()
					console.log('Audio track created and enabled')
				} else {
					this.mainAudioTrack.disable()
					console.log('Audio track created but disabled')
				}
			}
		},
		getMeetKey(): string {
			return window.location.href.split('meet/').pop()!.split('?')[0]
		},
		async loadRoomParticipants(): Promise<void> {
			// Evita chiamate multiple simultanee
			if (this.loadingParticipants) return

			try {
				this.loadingParticipants = true
				const roomId = this.getMeetKey()
				const response = await goapi.post('/twilio-participants', {
					roomName: roomId
				})
				const data = response.data
				if (response.status === 200 && data.participants) {
					this.partecipants = data.participants.map((participant: { name: string }) => ({
						name: participant.name,
						avatarColor: this.generateAvatarColor(participant.name.split('|')[0])
					}))

					console.log(`Loaded ${data.participants.length} participants for room ${roomId}`)
				} else {
					// Room non trovata o errore - mantieni lista vuota
					console.log('Failed to load participants, response not ok:', response.status)
					this.partecipants = []
				}
			} catch (error) {
				console.warn('Error loading room participants:', error)
				// In caso di errore, mantieni lista vuota
				this.partecipants = []
			} finally {
				this.loadingParticipants = false
			}
		},
		generateAvatarColor(name: string): string {
			const colors = [
				'#FFC312', // Giallo Oro Vivace
				'#FF8C42', // Arancio Mandarino
				'#E74C3C', // Rosso Rubino
				'#FF6B9D', // Rosa Fucsia Vivido
				'#A55EEA', // Viola Ametista
				'#9B59B6', // Viola Orchidea
				'#4A90E2', // Blu Reale Luminoso
				'#5DADE2', // Azzurro Cielo Elettrico
				'#26C281', // Verde Smeraldo
				'#A3CB38' // Verde Lime Brillante
			]

			// Funzione di hash FNV-1a con gestione corretta degli overflow
			let hash = 2166136261
			for (let i = 0; i < name.length; i++) {
				hash ^= name.charCodeAt(i)
				hash = Math.imul(hash, 16777619) // Moltiplicazione a 32-bit
			}

			// Restituisce il colore corrispondente all'indice calcolato
			return colors[Math.abs(hash) % colors.length]
		},
		getName(item: { name?: string }): string {
			if (item.name) {
				const nameParts = item.name.split(' ')
				const name = nameParts[0].substring(0, 1).toUpperCase() + nameParts[1].substring(0, 1).toUpperCase()
				return name
			}
			return ''
		},
		getNameAndSurname(item: { name?: string }): string {
			if (item.name) {
				return item.name
					.split(' ')
					.map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
					.join(' ')
			}
			return ''
		},
		async createVideoTrack(): Promise<void> {
			this.checkVideoLoading = true
			try {
				if (!window.TwilioVideo) {
					const TwilioVideo = await import('twilio-video')
					window.TwilioVideo = TwilioVideo
				}

				const { createLocalVideoTrack } = window.TwilioVideo
				const constraints = getPlatformConstraints(true, false).video

				console.log('Creating LocalVideoTrack with constraints:', constraints)
				this.mainVideoTrack = await createLocalVideoTrack(constraints)
				console.log('LocalVideoTrack created:', this.mainVideoTrack)

				const videoElement = this.$refs.video as HTMLVideoElement
				if (videoElement) {
					this.mainVideoTrack.attach(videoElement)
					console.log('Video track attached to video element')
				}

				this.enableVideo = true
				this.permissions.video = true
				console.log('LocalVideoTrack created successfully')
				this.checkVideoLoading = false
			} catch (error) {
				console.error('Failed to create video track:', error)
				this.enableVideo = false
				this.permissions.video = false
				this.mainVideoTrack = null
			}
		},

		isKrispSupported(): boolean {
			// Check browser support for Krisp based on Twilio documentation
			const userAgent = navigator.userAgent

			// Supported browsers: Chrome 66+, Edge 79+, Firefox 76+, Chrome on Android 66+
			const isChrome = /Chrome/.test(userAgent) && !/Edge/.test(userAgent)
			const isEdge = /Edge/.test(userAgent) || /Edg/.test(userAgent)
			const isFirefox = /Firefox/.test(userAgent)

			// Explicitly NOT supported: Safari, iOS
			const isSafari = /Safari/.test(userAgent) && !/Chrome/.test(userAgent)
			const isIOS = /iPad|iPhone|iPod/.test(userAgent)

			if (isSafari || isIOS) {
				console.log('🚫 [KRISP] Browser not supported (Safari/iOS), using regular audio track')
				return false
			}

			if (isChrome || isEdge || isFirefox) {
				console.log('✅ [KRISP] Browser supported, enabling Krisp noise cancellation')
				return true
			}

			console.log('⚠️ [KRISP] Unknown browser compatibility, trying Krisp anyway')
			return true // Try anyway for unknown browsers
		},

		async createAudioTrack(): Promise<void> {
			try {
				// Ensure Twilio Video is loaded
				if (!window.TwilioVideo) {
					console.log('Loading Twilio Video library...')
					const TwilioVideo = await import('twilio-video')
					window.TwilioVideo = TwilioVideo
				}

				const { createLocalAudioTrack } = window.TwilioVideo

				// Check if Krisp is supported on this browser
				const useKrisp = this.isKrispSupported()

				console.log('Creating LocalAudioTrack with Krisp:', useKrisp)

				if (useKrisp) {
					// Create audio track with Krisp noise cancellation
					this.mainAudioTrack = await createLocalAudioTrack({
						audio: {
							echoCancellation: true,
							noiseSuppression: false, // Disable browser's built-in noise suppression when using Krisp
							autoGainControl: true
						},
						noiseCancellationOptions: {
							sdkAssetsPath: '/krisp',
							vendor: 'krisp'
						}
					})
					console.log('🎯 [KRISP] LocalAudioTrack created with Krisp noise cancellation')
				} else {
					// Fallback: create regular audio track
					this.mainAudioTrack = await createLocalAudioTrack({
						audio: {
							echoCancellation: true,
							noiseSuppression: true, // Use browser's noise suppression as fallback
							autoGainControl: true
						}
					})
					console.log('⚪ [KRISP] LocalAudioTrack created without Krisp (fallback)')
				}

				this.enableAudio = true
				this.permissions.audio = true
				console.log('LocalAudioTrack created successfully')
			} catch (error) {
				console.error('Failed to create audio track:', error)
				this.enableAudio = false
				this.permissions.audio = false
				this.mainAudioTrack = null
			}
		},

		// Aggiorna anche i metodi toggle per mantenere lo stato sincronizzato:

		async toggleVideo(): Promise<void> {
			console.log('preview toggleVideo start', 'enableVideo:', this.enableVideo)
			this.enableVideo = !this.enableVideo

			if (!this.enableVideo) {
				// DISABLING VIDEO - mantieni il permesso true se era già concesso
				if (this.mainVideoTrack) {
					this.mainVideoTrack.stop()
					this.mainVideoTrack = null
				}

				const mediaControl = document.querySelector('video') as HTMLVideoElement
				if (mediaControl) {
					mediaControl.srcObject = null
				}
				// NON toccare permissions.video - rimane com'era
			} else {
				// ENABLING VIDEO
				await this.createVideoTrack()
				// permissions.video viene aggiornato da createVideoTrack

				await this.reapplyActiveFiltersInPreview()
			}
			console.log('preview toggleVideo end', 'enableVideo:', this.enableVideo)
		},

		async toggleAudio(): Promise<void> {
			console.log('preview toggleAudio start')
			this.enableAudio = !this.enableAudio

			// With unified audio management, we always have a track
			// We just enable/disable it based on user preference
			if (this.mainAudioTrack) {
				if (this.enableAudio) {
					// ENABLING AUDIO
					this.mainAudioTrack.enable()
					console.log('Audio track enabled')
				} else {
					// DISABLING AUDIO
					this.mainAudioTrack.disable()
					console.log('Audio track disabled')
				}
			} else {
				console.warn('No audio track available to toggle')
			}
			console.log('preview toggleAudio end')
		},
		async openModal(): Promise<void> {
			this.oldEffects = {
				blur: this.blur,
				background: this.background
			}
			this.modal = true

			// Initialize video processors if not already done
			if (!this.videoProcessors) {
				await this.initializeVideoProcessors()
			}
		},
		resetModal(): void {
			this.removeAllEffects()
			this.modal = false
		},
		closeModal(save: boolean): void {
			if (!save) {
				this.blur = this.oldEffects.blur
				this.background = this.oldEffects.background
				if (this.blur === 0 && !this.background) {
					this.removeAllEffects()
				} else {
					this.applyBlurProcessor(this.blur)
					this.applyVirtualBackgroundProcessor(this.background)
				}
			}
			console.log('Closing background modal')
			this.modal = false
		},
		async initializeVideoProcessors(): Promise<void> {
			try {
				console.log('Initializing video processors')
				const VideoProcessors = await import('@twilio/video-processors')
				this.videoProcessors = VideoProcessors
				console.log('Video processors initialized successfully')
			} catch (error) {
				console.error('Failed to initialize video processors:', error)
			}
		},
		async removeCurrentProcessor(): Promise<void> {
			if (!this.currentProcessor || !this.mainVideoTrack) {
				console.log('No current processor or video track to remove processor from')
				return
			}

			try {
				console.log('Removing current processor from video track')

				// Try to remove processor with better error handling
				try {
					await this.mainVideoTrack.removeProcessor(this.currentProcessor)
					console.log('Processor successfully removed from video track')
				} catch (removeError) {
					console.warn('Error removing processor from track:', removeError)
					// If removal fails, check if track has any processors and try to clear them all
					if (this.mainVideoTrack.processors && this.mainVideoTrack.processors.length > 0) {
						console.log('Attempting to remove all processors from track')
						const processors = [...this.mainVideoTrack.processors]
						for (const processor of processors) {
							try {
								await this.mainVideoTrack.removeProcessor(processor)
								console.log('Removed processor during cleanup')
							} catch (err) {
								console.warn('Failed to remove processor during cleanup:', err)
							}
						}
					}
				}

				// Clean up processor references
				await this.cleanupProcessors()
			} catch (error) {
				console.error('Error in removeCurrentProcessor:', error)
				// Force cleanup even if removal fails
				await this.cleanupProcessors()
			}
		},
		async removeAllEffects(): Promise<void> {
			this.blur = 0
			this.background = null

			await this.removeCurrentProcessor()
		},
		async applyBlurProcessor(blurLevel: number): Promise<void> {
			if (!this.videoProcessors || !this.mainVideoTrack) {
				console.warn('Video processors or main video track not available')
				return
			}

			try {
				console.log('Applying blur processor with level:', blurLevel)

				// Remove any existing processor first
				await this.removeCurrentProcessor()
				// Create new blur processor if blur level > 0
				if (blurLevel > 0) {
					const { GaussianBlurBackgroundProcessor } = this.videoProcessors
					const blurFilterRadius = getBlurFilterRadius(blurLevel)

					console.log('Creating GaussianBlurBackgroundProcessor with radius:', blurFilterRadius)
					this.currentProcessor = new GaussianBlurBackgroundProcessor({
						assetsPath: window.location.origin + '/video-processors',
						blurFilterRadius: blurFilterRadius
					})

					// Load the ML model
					await this.currentProcessor.loadModel()
					console.log('Blur model loaded successfully')

					// Apply processor directly to main video track
					await this.mainVideoTrack.addProcessor(this.currentProcessor)
					console.log('Blur processor applied to main video track')
				}

				// Update blur state
				this.blur = blurLevel
				this.background = null // Clear background when applying blur
			} catch (error) {
				console.error('Failed to apply blur processor:', error)
			}
		},
		async applyVirtualBackgroundProcessor(backgroundImage: BackgroundImage | null): Promise<void> {
			if (!this.videoProcessors || !this.mainVideoTrack) {
				console.warn('Video processors or main video track not available')
				return
			}

			if (!backgroundImage || !backgroundImage.image) {
				// Clear virtual background - remove all effects
				await this.removeAllEffects()
				return
			}

			try {
				this.backgroundsLoading = true
				console.log('Applying virtual background processor with image:', backgroundImage.image)

				// Remove any existing processor first
				await this.removeCurrentProcessor()

				// Load the background image as HTMLImageElement
				const imageElement = await this.loadBackgroundImage(backgroundImage.image)

				// Create virtual background processor
				const { VirtualBackgroundProcessor } = this.videoProcessors
				this.virtualBackgroundProcessor = new VirtualBackgroundProcessor({
					assetsPath: window.location.origin + '/video-processors',
					backgroundImage: imageElement
				})

				// Set as current processor
				this.currentProcessor = this.virtualBackgroundProcessor

				console.log('Loading virtual background model...')
				await this.currentProcessor.loadModel()
				console.log('Virtual background model loaded successfully')

				// Apply processor directly to main video track
				await this.mainVideoTrack.addProcessor(this.currentProcessor)
				console.log('Virtual background processor applied to main video track')

				// Update state
				this.background = backgroundImage
				this.blur = 0 // Clear blur when applying virtual background
				this.backgroundsLoading = false
			} catch (error) {
				console.error('Failed to apply virtual background processor:', error)
				this.backgroundsLoading = false
			}
		},
		async loadBackgroundImage(imageSrc: string): Promise<HTMLImageElement> {
			return new Promise((resolve, reject) => {
				const img = new Image()
				img.crossOrigin = 'anonymous'
				img.onload = () => {
					console.log('Background image loaded successfully:', imageSrc)
					resolve(img)
				}
				img.onerror = (error) => {
					console.error('Failed to load background image:', imageSrc, error)
					reject(new Error(`Failed to load background image: ${imageSrc}`))
				}
				img.src = imageSrc
			})
		},
		async reapplyActiveFiltersInPreview(): Promise<void> {
			try {
				console.log('=== REAPPLYING ACTIVE FILTERS IN PREVIEW ===')
				console.log('Current filter state in Preview:', {
					blur: this.blur,
					background: this.background?.image || null,
					hasMainVideoTrack: !!this.mainVideoTrack
				})

				// Ensure we have a video track to apply filters to
				if (!this.mainVideoTrack) {
					console.log('No video track available, skipping filter reapplication')
					return
				}

				// Wait a bit for the video track to be fully ready
				await new Promise((resolve) => setTimeout(resolve, 500))

				// Initialize video processors if needed
				if (!this.videoProcessors) {
					console.log('Initializing video processors for filter reapplication')
					await this.initializeVideoProcessors()
				}

				// Apply currently active filters with priority: background > blur
				if (this.background && this.background.image) {
					// Apply virtual background
					console.log('Reapplying virtual background in Preview:', this.background.image)
					await this.applyVirtualBackgroundProcessor(this.background)
					console.log('Virtual background reapplied successfully in Preview')
				} else if (this.blur && this.blur > 0) {
					// Apply blur filter
					console.log('Reapplying blur filter in Preview, level:', this.blur)
					await this.applyBlurProcessor(this.blur)
					console.log('Blur filter reapplied successfully in Preview')
				} else {
					console.log('No active filters to reapply in Preview')
				}

				console.log('=== PREVIEW FILTER REAPPLICATION COMPLETED ===')
			} catch (error) {
				console.error('Failed to reapply active filters in Preview:', error)
				// Retry once after a delay
				setTimeout(async () => {
					console.log('Retrying filter reapplication in Preview...')
					try {
						if (this.background && this.background.image) {
							await this.applyVirtualBackgroundProcessor(this.background)
						} else if (this.blur && this.blur > 0) {
							await this.applyBlurProcessor(this.blur)
						}
						console.log('Preview filter reapplication retry completed')
					} catch (retryError) {
						console.error('Preview filter reapplication retry failed:', retryError)
					}
				}, 1000)
			}
		},
		async cleanupProcessors(): Promise<void> {
			try {
				console.log('Cleaning up processors')

				// Clean up current processor
				if (this.currentProcessor) {
					try {
						if (typeof this.currentProcessor.destroy === 'function') {
							await this.currentProcessor.destroy()
							console.log('Current processor destroyed')
						}
					} catch (error) {
						console.warn('Error destroying current processor:', error)
					}
					this.currentProcessor = null
				}

				// Clean up virtual background processor (if different from current)
				if (this.virtualBackgroundProcessor && this.virtualBackgroundProcessor !== this.currentProcessor) {
					try {
						if (typeof this.virtualBackgroundProcessor.destroy === 'function') {
							await this.virtualBackgroundProcessor.destroy()
							console.log('Virtual background processor destroyed')
						}
					} catch (error) {
						console.warn('Error destroying virtual background processor:', error)
					}
				}
				this.virtualBackgroundProcessor = null

				console.log('Processors cleaned up successfully')
			} catch (error) {
				console.error('Error cleaning up processors:', error)
			}
		},
		async cleanupTracks(): Promise<void> {
			try {
				// If we're joining, don't stop the tracks - they're being transferred to the meeting
				if (this.isJoining) {
					console.log('Skipping track cleanup during join - tracks transferred to meeting')
					// Still clean up processors, but don't stop tracks
					await this.cleanupProcessors()
					// Clear references without stopping
					this.mainVideoTrack = null
					this.mainAudioTrack = null
					return
				}

				// Remove any processors first
				if (this.currentProcessor && this.mainVideoTrack) {
					try {
						// Check if processor is actually applied to the track
						const processors = this.mainVideoTrack.processors || []
						if (processors.includes(this.currentProcessor)) {
							await this.mainVideoTrack.removeProcessor(this.currentProcessor)
							console.log('Processor removed before cleanup')
						} else {
							console.log('Processor not found on track during cleanup, skipping removal')
						}
					} catch (error) {
						console.warn('Error removing processor during cleanup:', error)
					}
				}

				// Clean up processors
				await this.cleanupProcessors()

				// Stop and clean up tracks
				if (this.mainVideoTrack) {
					this.mainVideoTrack.stop()
					this.mainVideoTrack = null
					console.log('Main video track stopped and cleaned up')
				}

				if (this.mainAudioTrack) {
					this.mainAudioTrack.stop()
					this.mainAudioTrack = null
					console.log('Main audio track stopped and cleaned up')
				}

				console.log('All tracks cleaned up successfully')
			} catch (error) {
				console.error('Error cleaning up tracks:', error)
			}
		},
		validateEmail() {
			this.emailErrors = false
			if (this.partecipantEmail && this.partecipantEmail.trim()) {
				const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
				if (!emailRegex.test(this.partecipantEmail.trim())) {
					this.emailErrors = true
				}
			}
		},
		onJoin(): void {
			if (this.partecipantName && this.partecipantSurname && this.partecipantEmail) {
				this.errors = ''
				// Validate email if provided
				if (this.partecipantEmail && this.partecipantEmail.trim()) {
					const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
					if (!emailRegex.test(this.partecipantEmail.trim())) {
						this.emailErrors = true
						return
					}
				}

				// Create identity with optional email pattern
				let participantIdentity = this.partecipantName.trim() + ' ' + this.partecipantSurname.trim()
				if (this.partecipantEmail && this.partecipantEmail.trim()) {
					participantIdentity += '|' + this.partecipantEmail.trim()
				}

				// Check for existing participants (only by name, not email)
				const nameOnly = this.partecipantName.trim() + ' ' + this.partecipantSurname.trim()
				const alreadyExist = this.partecipants.find((p) => {
					// Parse participant name (handle both old format and new format with email)
					const participantName = p.name.includes('|') ? p.name.split('|')[0] : p.name
					return participantName === nameOnly
				})

				if (!alreadyExist) {
					// Set joining flag to prevent track cleanup
					this.isJoining = true

					// Prepare participant data with LocalTracks and filter state
					const participantData = {
						partecipantName: participantIdentity,
						enableVideo: this.enableVideo,
						enableAudio: this.enableAudio,
						meetName: this.meetName,
						videoTrack: this.mainVideoTrack,
						audioTrack: this.mainAudioTrack,
						// Transfer filter state from Preview to Meeting
						currentBlurLevel: this.blur,
						currentBackground: this.background,
						hasCurrentProcessor: !!this.currentProcessor,
						currentProcessor: this.currentProcessor
					}
					console.log('Joining participant data:', participantData)
					console.log('Joining with LocalTracks and filter state:', {
						video: !!this.mainVideoTrack,
						audio: !!this.mainAudioTrack,
						hasProcessors: !!this.currentProcessor,
						blurLevel: this.blur,
						background: this.background?.image || null,
						identity: participantIdentity
					})

					this.$emit('join', participantData)
				} else {
					this.errors = this.$t('partecipant_already_exist') + '!'
				}
			} else {
				this.errors = this.$t('allFieldsRequired').toString()
			}
		}
	},
	async beforeDestroy(): Promise<void> {
		document.body.style.overflow = ''
		// Pulisci l'interval per il polling dei partecipanti
		if (this.participantsInterval) {
			clearInterval(this.participantsInterval)
		}

		// Cleanup video tracks and processors
		await this.cleanupTracks()
	}
})
